import { AuthContext, CommonContext } from '@repo-level/core-front';
import type { PlayerBonus, PlayerFreespin } from 'api/common';
import { AddonsContext } from 'contexts/addons';
import { RealTimeServiceContext } from 'contexts/realTimeService';
import { showTooltip } from 'helpers/tooltip';
import { getReceivedBonusTooltipConfig } from 'modules/bonuses/components/ReceivedBonusTooltip';
import {
  getBonusesLastSeenAt,
  getSeenTimestamp,
  saveBonusesLastSeenAt,
} from 'modules/bonuses/helpers/lastSeen';
import { isUnseenBonus } from 'modules/bonuses/helpers/unseenBonuses';
import { isWagerFreeActivatedCash } from 'modules/bonuses/helpers/wagerFreeCash';
import { WheelStatusEnum } from 'modules/wheel/components/helpers';
import type { FC } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

interface IBonusesNotificationContextData {
  hasUnseenBonuses: boolean;
  setIsViewingMyBonuses: (isViewing: boolean) => void;
  suppressWagerFreeToast: () => void;
  clearWagerFreeSuppression: () => void;
}

const WAGER_FREE_SUPPRESSION_MS = 30_000;

export const BonusesNotificationContext =
  createContext<IBonusesNotificationContextData>(null as any);

export const BonusesNotificationContextProvider: FC = ({ children }) => {
  const { t } = useTranslation();

  const { user } = useContext(AuthContext);
  const { project } = useContext(CommonContext);
  const {
    bonuses,
    bonusesPending,
    freespins,
    freespinsPending,
    receivedBonus,
    fetchBonuses,
    resetReceivedBonus,
  } = useContext(RealTimeServiceContext);
  const { wheelStatus } = useContext(AddonsContext);

  const userId = user?.id;
  const isServerStorageEnabled = project?.userStorageItems ?? false;
  const isBonusesLoaded = !(bonusesPending || freespinsPending);

  const [lastSeenAt, setLastSeenAt] = useState<string | null>(null);
  const [isLastSeenAtLoaded, setIsLastSeenAtLoaded] = useState(false);
  const [isViewingMyBonuses, setIsViewingMyBonuses] = useState(false);
  const [deferredBonus, setDeferredBonus] = useState<
    PlayerBonus | PlayerFreespin | null
  >(null);
  const [isPageHidden, setIsPageHidden] = useState(() => document.hidden);
  const wagerFreeSuppressedUntilRef = useRef(0);

  const isWheelSpinning =
    wheelStatus === WheelStatusEnum.Loading ||
    wheelStatus === WheelStatusEnum.Spinning;

  useEffect(() => {
    setLastSeenAt(null);
    setIsLastSeenAtLoaded(false);

    if (!userId) return;

    let isCancelled = false;

    (async () => {
      const timestamp = await getBonusesLastSeenAt(
        userId,
        isServerStorageEnabled
      );

      if (isCancelled) return;

      setLastSeenAt(timestamp);
      setIsLastSeenAtLoaded(true);
    })();

    return () => {
      isCancelled = true;
    };
  }, [userId, isServerStorageEnabled]);

  const hasUnseen = useMemo(() => {
    if (!(userId && isLastSeenAtLoaded && isBonusesLoaded)) return false;

    const lastSeenTime =
      lastSeenAt === null ? 0 : new Date(lastSeenAt).getTime();
    const isUnseen = (bonus: PlayerBonus | PlayerFreespin) =>
      isUnseenBonus(bonus, lastSeenTime);

    return bonuses.some(isUnseen) || freespins.some(isUnseen);
  }, [
    userId,
    isLastSeenAtLoaded,
    isBonusesLoaded,
    bonuses,
    freespins,
    lastSeenAt,
  ]);

  const markBonusesAsSeen = useCallback(() => {
    if (!userId) return;

    const timestamp = getSeenTimestamp(bonuses, freespins);

    setLastSeenAt(timestamp);
    void saveBonusesLastSeenAt(userId, isServerStorageEnabled, timestamp);
  }, [userId, isServerStorageEnabled, bonuses, freespins]);

  useEffect(() => {
    if (!(isViewingMyBonuses && isLastSeenAtLoaded && hasUnseen)) return;

    markBonusesAsSeen();
  }, [isViewingMyBonuses, isLastSeenAtLoaded, hasUnseen, markBonusesAsSeen]);

  const suppressWagerFreeToast = useCallback(() => {
    wagerFreeSuppressedUntilRef.current =
      Date.now() + WAGER_FREE_SUPPRESSION_MS;
  }, []);

  const clearWagerFreeSuppression = useCallback(() => {
    wagerFreeSuppressedUntilRef.current = 0;
  }, []);

  const consumeWagerFreeSuppression = useCallback(
    (bonus: PlayerBonus | PlayerFreespin) => {
      if (!isWagerFreeActivatedCash(bonus)) return false;

      const isSuppressed = Date.now() < wagerFreeSuppressedUntilRef.current;
      wagerFreeSuppressedUntilRef.current = 0;

      return isSuppressed;
    },
    []
  );

  const showBonusReceivedTooltip = useCallback(
    (bonus: PlayerBonus | PlayerFreespin) => {
      showTooltip(getReceivedBonusTooltipConfig(bonus, t));
    },
    [t]
  );

  useEffect(() => {
    if (!receivedBonus) return;

    resetReceivedBonus();

    if (consumeWagerFreeSuppression(receivedBonus)) return;

    if (isViewingMyBonuses && !isWagerFreeActivatedCash(receivedBonus)) return;

    // the wheel keeps spinning for a few seconds after the bonus arrives
    if (isPageHidden || isWheelSpinning) {
      setDeferredBonus(receivedBonus);
      return;
    }

    showBonusReceivedTooltip(receivedBonus);
  }, [
    receivedBonus,
    isViewingMyBonuses,
    isPageHidden,
    isWheelSpinning,
    resetReceivedBonus,
    consumeWagerFreeSuppression,
    showBonusReceivedTooltip,
  ]);

  useEffect(() => {
    if (!(deferredBonus && !isPageHidden && !isWheelSpinning)) return;

    setDeferredBonus(null);
    showBonusReceivedTooltip(deferredBonus);
  }, [deferredBonus, isPageHidden, isWheelSpinning, showBonusReceivedTooltip]);

  useEffect(() => {
    if (!userId) return;

    const handleVisibilityChange = () => {
      setIsPageHidden(document.hidden);

      if (document.hidden) return;

      fetchBonuses();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, fetchBonuses]);

  const hasUnseenBonuses = hasUnseen && !isViewingMyBonuses && !isWheelSpinning;

  const value = useMemo(
    () => ({
      hasUnseenBonuses,
      setIsViewingMyBonuses,
      suppressWagerFreeToast,
      clearWagerFreeSuppression,
    }),
    [hasUnseenBonuses, suppressWagerFreeToast, clearWagerFreeSuppression]
  );

  return (
    <BonusesNotificationContext.Provider value={value}>
      {children}
    </BonusesNotificationContext.Provider>
  );
};
